import { useEffect, useLayoutEffect, useRef } from "react";

import type { CopilotChatMessage } from "src/cases/copilot/useCaseCopilotSession";

type UseChatScrollParams = {
  listRef: React.RefObject<HTMLUListElement>;
  messages: CopilotChatMessage[];
  isActive: boolean;
  failureMessage: string | null;
  isFetchingNextPage: boolean;
};

/**
 * Encapsulates scroll behaviour for the copilot chat list:
 * - Anchor-scroll that pins the user's last message while the AI responds
 */
export const useChatScrollAnchor = ({
  listRef,
  messages,
  isActive,
  failureMessage,
  isFetchingNextPage,
}: UseChatScrollParams) => {
  const anchorRef = useRef<string | null>(null);
  const anchorRafRef = useRef(0);

  // Anchor scroll: pin user's message as AI responds.
  useEffect(() => {
    cancelAnimationFrame(anchorRafRef.current);

    const list = listRef.current;
    if (!list || !anchorRef.current) return;

    const messageIndex = messages.findLastIndex((m) => m.role === "user");
    if (messageIndex < 0) return;

    const childOffset = isFetchingNextPage ? 1 : 0; // skeleton
    const element = list.children[messageIndex + childOffset] as
      | HTMLElement
      | undefined;
    if (!element) return;

    anchorRafRef.current = requestAnimationFrame(() => {
      const anchorOffset =
        element.getBoundingClientRect().top -
        list.getBoundingClientRect().top +
        list.scrollTop;

      const maxScroll = Math.max(0, list.scrollHeight - list.clientHeight);
      list.scrollTop = Math.min(Math.max(0, anchorOffset - 12), maxScroll);
    });

    return () => cancelAnimationFrame(anchorRafRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- omit isActive to avoid stale scroll
  }, [messages, isFetchingNextPage]);

  // Clear anchor after AI responds. Declared AFTER anchor scroll so
  // React queues the rAF before this effect clears the ref.
  useEffect(() => {
    if (!anchorRef.current || isActive) return;

    const lastUserIdx = messages.findLastIndex((m) => m.role === "user");
    const hasAIResponse = lastUserIdx >= 0 && lastUserIdx < messages.length - 1;

    if (hasAIResponse || failureMessage) {
      anchorRef.current = null;
    }
  }, [isActive, messages, failureMessage]);

  return {
    activateAnchor: () => {
      anchorRef.current = "active";
    },
    clearAnchor: () => {
      anchorRef.current = null;
    },
  };
};

type UseChatSetScrollToBottomParams = {
  listRef: React.RefObject<HTMLUListElement>;
  messages: CopilotChatMessage[];
  isLoading: boolean;
};

export const useSetScrollToBottom = ({
  listRef,
  messages,
  isLoading,
}: UseChatSetScrollToBottomParams) => {
  const hasHandledInitialFetch = useRef(false);

  useLayoutEffect(() => {
    if (
      listRef.current &&
      !hasHandledInitialFetch.current &&
      !isLoading &&
      messages.length > 0
    ) {
      hasHandledInitialFetch.current = true;
      const scrollToBottom = () => {
        if (listRef.current) {
          listRef.current.scrollTop = listRef.current.scrollHeight;
        }
      };
      // Scroll again after paint in case the floating window hasn't settled yet.
      scrollToBottom();
      requestAnimationFrame(scrollToBottom);
    }
  }, [messages.length, isLoading]);
};
